import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { saveAs } from 'file-saver';
import moment from 'moment';
import axios from 'axios';
import JSZip from 'jszip';
import * as Yup from 'yup';
import path from 'path';
import { connect } from 'react-redux';

import {
	Table,
	Menu,
	Dropdown,
	Button,
	Icon,
	Select,
	Modal,
	Typography,
	DatePicker,
	message,
} from 'antd';

import Fallback from '../Fallback';
import FileExt from '../FileExt';
import FileDuration from '../FileDuration';
import PageSizeHandler from '../PageSizeHandle';
import Progress from '../Progress';
import PlayCell from '../PlayCell';
import Form from '../Form';
import { FiltersContainer, TableActions } from './styles';

import BlitzAPI from '../../services/sdks/blitz';
import { useElement, usePlayer, useDownload, useFilesValidator } from '../../hooks';
import { FiDownload, FiPlus, FiPlusCircle, FiTrash2, FiUpload, FiXCircle } from 'react-icons/fi';
import { resolveFileSrc } from '../../helpers/fileSrcResolver';
import FilesUploader from '../FilesUploader';
import env from '../../config/env';

const LocalBlitz = ({ user, program }) => {
	const player = usePlayer();
	const download = useDownload();

	const {
		categorizedTypes: { blitz: categories },
	} = useElement();

	const [fallback, setFallback] = useState({ initialData: false });
	const [blitz, setBlitz] = useState([]);
	const [selectedBlitz, setSelectedBlitz] = useState([]);
	const [visibleModals, setVisibleModals] = useState({ addBlitz: false });
	const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: null });
	const [progress, setProgress] = useState(null);
	const [filters, setFilters] = useState({ category: undefined, releaseDate: null });
	const [isValidating, setIsValidating] = useState(false);
	const { hasValidationError } = useFilesValidator();
	const [newBlitz, setNewBlitz] = useState({
		category: undefined,
		releaseDate: null,
		files: [],
	});

	const columns = [
		{
			title: 'Categoria',
			key: 'category',
			render: (blitz) => (
				<PlayCell
					meta={{ primary: blitz?.category }}
					isPlaying={player?.ref === blitz?._id && player?.isPlaying}
					onPause={player.resume}
					onPlay={() => {
						player.start({
							src: resolveFileSrc({ fileName: blitz.filename, bucket: env.miscS3Bucket }),
							ref: blitz?._id,
							meta: {
								name: `BLITZ LOCAL - ${blitz.category}`,
								artist: blitz?.program?.name,
							},
						});
					}}
				/>
			),
		},
		{
			title: 'Lançamento',
			dataIndex: 'releaseDate',
			key: 'date',
		},
		{
			title: 'Duração',
			align: 'center',
			key: 'duration',
			render: (blitz) => <FileDuration src={resolveFileSrc({ fileName: blitz.filename, bucket: env.miscS3Bucket })} />,
		},
		{
			title: 'Formato',
			align: 'center',
			key: 'ext',
			render: (blitz) => <FileExt src={blitz?.filename} />,
		},
		{
			key: 'id',
			title: 'ID',
			align: 'center',
			render: ({ _id }) => (
				<Typography.Text title={_id} copyable={{ text: _id }}>{`${_id.slice(
					0,
					10
				)}...`}</Typography.Text>
			),
		},
		{
			title: 'Ações',
			align: 'right',
			key: 'options',
			render: (blitz) => (
				<>
					<Dropdown
						placement='bottomRight'
						overlay={
							<Menu>
								<Menu.Item
									key='play'
									onClick={() => {
										player.start({
											src: resolveFileSrc({ fileName: blitz.filename, bucket: env.miscS3Bucket }),
											ref: blitz?._id,
											meta: {
												artist: 'BLITZ LOCAL',
												name: blitz.category,
											},
										});
									}}>
									<Icon type='play-circle' /> Reproduzir
								</Menu.Item>
								<Menu.Item key='download' onClick={() => handleDownload({ blitz })}>
									<Icon type='download' /> Fazer download
								</Menu.Item>

								<Menu.Divider />

								<Menu.Item
									key='delete'
									className='ant-dropdown-menu-item-danger'
									onClick={() => {
										Modal.confirm({
											title: 'Excluir off?',
											icon: 'exclamation-circle',
											content: 'Essa ação não poderá ser revertida, deseja continuar mesmo assim?',
											okText: 'Excluir',
											onOk: () => handleDelete({ blitzId: blitz?._id }),
											okButtonProps: {
												icon: 'delete',
												type: 'danger',
											},
											cancelText: 'Cancelar',
											cancelButtonProps: {
												icon: 'close-circle',
											},
										});
									}}>
									<Icon type='delete' /> Excluir
								</Menu.Item>
							</Menu>
						}>
						<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 16 }} type='more' />
					</Dropdown>
				</>
			),
		},
	];

	const handleDownload = useCallback(
		async ({ blitz }) => {
			try {
				const name = `BLITZ LOCAL - ${blitz?.category}`;

				return download({ filename: blitz?.filename, name });
			} catch (error) {
				console.error(error);
				message.error('Não foi possível fazer o download.');
			}
		},
		[download]
	);

	const handleDownloadAsZIP = useCallback(async () => {
		setFallback((prev) => ({ ...prev, multiDownload: true }));

		const zip = new JSZip();

		for (let index = 0; index < selectedBlitz.length; index++) {
			const blitz = selectedBlitz[index];
			const filePath = resolveFileSrc({ fileName: blitz.filename, bucket: env.miscS3Bucket });
			const blob = await axios.get(filePath, { responseType: 'blob' });
			const name = `BLITZ LOCAL - ${blitz?.category}`;
			const ext = path.extname(blitz?.filename);

			const fName = `${blitz?.program?.name} - ${name}${ext}`;

			zip.file(fName, blob.data, { binary: true });
		}

		const zipContent = await zip.generateAsync({ type: 'blob' });

		let downloadName = `BLITZ LOCAIS - ${program?.name}`;

		filters?.releaseDate && (downloadName = `${downloadName} - ${filters.releaseDate}`);

		saveAs(zipContent, downloadName);
		setFallback((prev) => ({ ...prev, multiDownload: false }));
	}, [program, selectedBlitz, filters]);

	const handleDelete = useCallback(async ({ blitzId }) => {
		try {
			setFallback((prev) => ({ ...prev, deleting: true }));

			await BlitzAPI.destroy({ blitzId });

			setBlitz((blitz) => blitz.filter(({ _id }) => _id !== blitzId));
		} catch (error) {
			console.error(error);
			message.error('Houve um erro ao deletar a blitz');
		} finally {
			setFallback((prev) => ({ ...prev, deleting: false }));
		}
	}, []);

	const handleMultiDelete = useCallback(async () => {
		for (let index = 0; index < selectedBlitz.length; index++) {
			const { _id: blitzId } = selectedBlitz[index];
			await handleDelete({ blitzId });
		}

		setSelectedBlitz([]);
	}, [selectedBlitz, handleDelete]);

	const handleCreateBlitz = useCallback(async () => {
		try {
			const validationSchema = Yup.object().shape({
				category: Yup.string().required('Informe a categoria'),
				program: Yup.string().required('Informe o programa'),
				user: Yup.string().required('Usuário inválido'),
				releaseDate: Yup.string().required('Informe a data de lançamento'),
				files: Yup.array().required('Selecione no mínimo 1 arquivo'),
			});

			const { category, releaseDate, files } = newBlitz;
			const data = {
				category,
				releaseDate,
				files,
				isLocal: true,
				user: user?._id,
				program: program?._id,
			};

			await validationSchema.validate(data);

			if (hasValidationError(files)) {
				return message.warning(
					'Alguns arquivos selecionados estão corrompidos. Por favor, substitua-os.'
				);
			}

			setFallback((prev) => ({ ...prev, uploadingBlitz: true }));

			const payload = new FormData();

			for (const key in data) {
				if (key === 'files') {
					data[key].forEach((file) => payload.append('file', file.data));
				} else {
					payload.append(key, data[key]);
				}
			}

			const {
				data: { blitz },
			} = await BlitzAPI.store({
				payload,
				onUploadProgress: ({ total, loaded }) => {
					setProgress(Math.floor((loaded * 100) / total) - 1);
				},
			});

			setProgress(100);
			setBlitz((prev) => [
				...prev,
				...blitz.map((n) => ({ ...n, program: { name: program?.name } })),
			]);

			setNewBlitz({ category: undefined, releaseDate: null, files: [] });
			setVisibleModals((prev) => ({ ...prev, addBlitz: false }));

			/** Aguarda 2 segundos para fechar a mensagem de sucesso do Progress */
			await new Promise((resolve) => setTimeout(() => resolve(), 2000));
		} catch (error) {
			if (error instanceof Yup.ValidationError) {
				return message.error(error.message);
			}

			console.error(error);
			message.error('Houve um erro, tente novamente');
		} finally {
			setProgress(null);
			setFallback((prev) => ({ ...prev, uploadingBlitz: false }));
		}
	}, [user, newBlitz, program, hasValidationError]);

	useEffect(() => {
		setPagination((prev) => ({ ...prev, current: 1 }));
	}, [filters]);

	useEffect(() => {
		const fetchblitz = async () => {
			try {
				setFallback((prev) => ({ ...prev, fetchingblitz: true }));

				let query = `isLocal=true&page=${pagination?.current - 1}&limit=${
					pagination?.pageSize
				}&program=${program?._id}&user=${user?._id}&`;

				filters?.releaseDate && (query = `${query}releaseDate=${filters.releaseDate}&`);
				filters?.category && (query = `${query}category=${filters.category}&`);

				const {
					data: { blitz, total },
				} = await BlitzAPI.index({ query });

				setPagination((prev) => ({ ...prev, total }));

				setBlitz(blitz);
				setFallback((prev) => ({ ...prev, fetchingblitz: false }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar as blitz');
			}
		};

		fetchblitz();
	}, [filters, pagination.current, pagination.pageSize, user]); //eslint-disable-line

	const isUploadButtonDisabled = useMemo(() => {
		return isValidating || !newBlitz.category || !newBlitz.releaseDate || !newBlitz.files.length;
	}, [isValidating, newBlitz]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<FiltersContainer>
				<div className='filters'>
					<Select
						showSearch
						value={filters.category}
						placeholder='Categoria'
						style={{ width: '200px', marginRight: '12px' }}
						onChange={(category) => setFilters({ ...filters, category })}
						filterOption={(input, { props: { _search } }) => {
							return _search.match(new RegExp(input, 'i'));
						}}>
						<Select.Option key='all' value={undefined} _search='TODOS'>
							TODOS
						</Select.Option>

						{categories.map((category) => (
							<Select.Option value={category} key={category}>
								{category}
							</Select.Option>
						))}
					</Select>

					<DatePicker
						placeholder='Data de lançamento'
						format='DD/MM/yyyy'
						style={{ width: '200px' }}
						value={filters?.releaseDate && moment(filters.releaseDate, 'DD/MM/yyyy')}
						onChange={(_, releaseDate) => setFilters({ ...filters, releaseDate })}
					/>
				</div>

				<Button onClick={() => setVisibleModals({ ...visibleModals, addBlitz: true })}>
					<FiPlus /> Novas blitz
				</Button>
			</FiltersContainer>

			<TableActions>
				<div className='actions'>
					<span>
						Quantidade: <strong>{pagination?.total}</strong>
					</span>
					<div>
						<Button
							size='small'
							disabled={!selectedBlitz.length}
							type='ghost'
							onClick={handleDownloadAsZIP}
							loading={fallback?.multiDownload}>
							<FiDownload /> Baixar selecionados{' '}
							{selectedBlitz.length !== 0 && `(${selectedBlitz.length})`}
						</Button>

						<Button
							size='small'
							disabled={!selectedBlitz.length}
							type='danger'
							onClick={() => {
								Modal.confirm({
									title: 'Deletar blitz selecionadas?',
									type: 'danger',
									content:
										'Todos as blitz selecionados serão excluídos e essa ação não poderá ser revertida, deseja continuar mesmo assim?',
									okText: 'Deletar',
									onOk: handleMultiDelete,
									okButtonProps: {
										icon: 'delete',
										type: 'danger',
									},
									cancelText: 'Cancelar',
									cancelButtonProps: {
										icon: 'close-circle',
									},
								});
							}}>
							<FiTrash2 /> Deletar selecionados{' '}
							{selectedBlitz.length !== 0 && `(${selectedBlitz.length})`}
						</Button>
					</div>
				</div>

				<PageSizeHandler pagination={pagination} setPagination={setPagination} />
			</TableActions>

			<Table
				loading={fallback?.fetchingblitz}
				size='middle'
				rowKey='_id'
				columns={columns}
				dataSource={blitz}
				style={{ border: 'none' }}
				pagination={{
					...pagination,
					size: 'large',
					onChange: (current) => setPagination({ ...pagination, current }),
				}}
				rowSelection={{
					onChange: (_, selectedRows) => setSelectedBlitz(selectedRows),
				}}
			/>

			<Modal
				width={600}
				visible={visibleModals?.addBlitz}
				onCancel={() => setVisibleModals({ ...visibleModals, addBlitz: false })}
				onOk={handleCreateBlitz}
				cancelButtonProps={{ disabled: fallback?.uploadingBlitz }}
				okButtonProps={{
					loading: fallback?.uploadingBlitz,
					disabled: isUploadButtonDisabled,
				}}
				okText={
					<>
						{!isValidating && <FiUpload />}
						{isValidating ? 'Validando Arquivos' : 'Fazer upload'}
					</>
				}
				cancelText={
					<>
						<FiXCircle /> Cancelar
					</>
				}
				title={
					<>
						<FiPlusCircle /> Adicionar blitz
					</>
				}>
				<Form.Container>
					<Form.Item label='Categoria'>
						<Select
							showSearch
							value={newBlitz?.category}
							placeholder='Categoria'
							style={{ marginBottom: 8, width: '100%' }}
							onChange={(category) => setNewBlitz({ ...newBlitz, category })}
							filterOption={(input, { props: { _search } }) => {
								return _search.match(new RegExp(input, 'i'));
							}}>
							{categories.map((category) => (
								<Select.Option key={category} value={category} _search={category}>
									{category}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label='Data de lançamento'>
						<DatePicker
							placeholder='Data de lançamento'
							format='DD/MM/yyyy'
							style={{ width: '100%' }}
							value={newBlitz?.releaseDate && moment(newBlitz.releaseDate, 'DD/MM/yyyy')}
							onChange={(_, releaseDate) => setNewBlitz({ ...newBlitz, releaseDate })}
						/>
					</Form.Item>

					<FilesUploader
						multiple
						onChange={(files) => setNewBlitz((prev) => ({ ...prev, files }))}
						onStartValidation={() => setIsValidating(true)}
						onEndValidation={() => setIsValidating(false)}
						validTypes={['.mp3', '.wav']}
					/>
				</Form.Container>
			</Modal>

			<Progress
				progress={progress}
				succesTitle='Blitz enviadas com sucesso'
				title='Enviando blitz, por favor aguarde...'
			/>
		</>
	);
};

export default connect(({ user }) => ({ user }))(LocalBlitz);
